import { HeaderDefinitions, VODCAST, DREAMKIDS, WATCH } from "../../constants";

// Helper functions
const getHeaderTypes = (contentType) => {
  return HeaderDefinitions[contentType]?.types || {};
};

const getLabelsForType = (contentType, headerType) => {
  return HeaderDefinitions[contentType]?.labels[headerType] || [];
};

const isValidLabel = (contentType, headerType, label) => {
  const labels = getLabelsForType(contentType, headerType);
  return labels.includes(label);
};

const getAllHeadersForContentType = (contentType) => {
  const headerDef = HeaderDefinitions[contentType];
  if (!headerDef) return null;

  return Object.entries(headerDef.labels).map(([type, labels]) => ({
    type,
    labels: [...labels],
  }));
};

const validateCSVHeaders = (csvArray, contentType) => {
  try {
    if (!Array.isArray(csvArray) || csvArray.length === 0) {
      throw new Error("Invalid CSV array: Array is empty or not an array");
    }

    // Get headers from first row
    const csvheadersObj = csvArray[0];
    const headers = Object.keys(csvheadersObj);

    let headerDefinition;
    const category = contentType.category || "";
    
    // Choose the right header definition based on both content type and category
    if (contentType === WATCH && category === VODCAST) {
      headerDefinition = HeaderDefinitions[VODCAST];
    } else if (contentType === WATCH && category === DREAMKIDS) {
      headerDefinition = HeaderDefinitions[DREAMKIDS];
    } else {
      headerDefinition = HeaderDefinitions[contentType];
    }
    
    if (!headerDefinition) {
      throw new Error(`Invalid content type: ${contentType}`);
    }
    
    // Determine required fields based on content type and category
    let requiredTypes = [];
    
    // Common required types for all content
    const commonRequiredTypes = ["title", "description", "duration", "year", "genre", "language"];
    
    if (contentType === WATCH) {
      if (category === VODCAST) {
        // For Vodcast, include host field as required
        requiredTypes = [...commonRequiredTypes, "host"];
      } else if (category === DREAMKIDS) {
        // For DreamKids, include age group and educational value
        requiredTypes = [...commonRequiredTypes, "ageGroup", "rating"];
      } else {
        // For regular Watch content
        requiredTypes = Object.values(headerDefinition.types);
      }
    } else {
      // For other content types
      requiredTypes = Object.values(headerDefinition.types);
    }

    const result = {
      isValid: true,
      errors: [],
      mappedHeaders: {},
      unmappedHeaders: [],
    };
    
    csvArray.forEach((row, rowIndex) => {
      headers.forEach((header, headerIndex) => {
        // Clean the header - remove quotes and trim whitespace if it's a string
        const cleanHeader =
          typeof header === "string"
            ? header.trim().replace(/['"]+/g, "")
            : String(header).trim();

        let headerMatched = false;

        // Check each type's labels
        for (const [type, labels] of Object.entries(headerDefinition.labels)) {
          if (labels.includes(cleanHeader)) {
            // Store the column index with its corresponding type
            if (!result.mappedHeaders[type]) {
              result.mappedHeaders[type] = [];
            }
            
            result.mappedHeaders[type].push({
              id: headerIndex + 1,
              label: cleanHeader,
              value: row[header],
            });
            
            headerMatched = true;
            break;
          }
        }

        if (!headerMatched && requiredTypes.includes(cleanHeader)) {
          result.isValid = false;
          result.errors.push(
            `Invalid header at position ${headerIndex + 1}: "${cleanHeader}"`
          );
          result.unmappedHeaders.push({
            index: headerIndex,
            label: cleanHeader,
          });
        }
      });
    });

    // Check if we have at least one header of each required type
    const missingTypes = requiredTypes.filter(
      (type) => !result.mappedHeaders[type]
    );

    if (missingTypes.length > 0) {
      result.isValid = false;
      result.errors.push(
        `Missing required field(s) in CSV file: ${missingTypes.join(", ")}`
      );
    }

    return result;
  } catch (error) {
    console.error("CSV validation error:", error);
    return {
      isValid: false,
      errors: [error.message],
      mappedHeaders: {},
      unmappedHeaders: [],
    };
  }
};

export {
  getHeaderTypes,
  getLabelsForType,
  isValidLabel,
  getAllHeadersForContentType,
  validateCSVHeaders,
};
